import { lazy, Suspense, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useProfile } from "@/context/ProfileContext";
import { useProductFirstRun } from "@/hooks/use-product-first-run";
import { useSalesTeams } from "@/hooks/queries/use-sales-teams";
import { useVerticalConfig } from "@/hooks/use-vertical-config";
import type { ProductKey } from "@/utils/productAccess";

// Wizards ship with the rare first-run path, not the main bundle.
const RecruitFirstRunWizard = lazy(() => import("./RecruitFirstRunWizard"));
const LeadsFirstRunWizard = lazy(() => import("./LeadsFirstRunWizard"));
const IntelligenceFirstRunWizard = lazy(
  () => import("./IntelligenceFirstRunWizard"),
);
const StoreFirstRunWizard = lazy(() => import("./StoreFirstRunWizard"));

const wizardFallback = (
  <div className="flex justify-center py-16">
    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
  </div>
);

interface ProductFirstRunGateProps {
  product: ProductKey;
  children: ReactNode;
}

/**
 * Shows a product's first-run wizard instead of the product until setup is
 * completed or dismissed. Rendered inside each product's entitlement guard,
 * so product access is already guaranteed here.
 *
 * The `?firstRun=<step>` query param forces the wizard open at a given step —
 * used to resume the Intelligence wizard after its apps-step OAuth round-trip.
 */
export default function ProductFirstRunGate({
  product,
  children,
}: ProductFirstRunGateProps) {
  const { businessProfile } = useProfile();
  const [searchParams] = useSearchParams();
  const firstRun = useProductFirstRun(product);
  const resumeStep = searchParams.get("firstRun");
  const { nav } = useVerticalConfig();

  const businessId = businessProfile?.uid;

  // Intelligence predates the wizard state: an existing team means the org
  // was configured the old way — never nag it. Query only while relevant.
  const needsLegacyTeamCheck =
    product === "intelligence" && firstRun.needsFirstRun && !resumeStep;
  const teamsQuery = useSalesTeams(
    needsLegacyTeamCheck ? businessId : undefined,
  );

  if (!firstRun.needsFirstRun || !businessId) {
    return <>{children}</>;
  }

  if (needsLegacyTeamCheck) {
    if (teamsQuery.isLoading) {
      return (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      );
    }
    if ((teamsQuery.data?.length ?? 0) > 0) {
      return <>{children}</>;
    }
  }

  const onDone = (opts?: { skipped?: boolean }) => {
    void firstRun.complete(opts);
  };

  return (
    <Suspense fallback={wizardFallback}>
      {product === "recruit" ? (
        <RecruitFirstRunWizard businessId={businessId} onDone={onDone} />
      ) : product === "leads" ? (
        <LeadsFirstRunWizard businessId={businessId} onDone={onDone} />
      ) : nav.firstRunWizard === "store" ? (
        // Grocery stores (GRA-87): upload a report instead of connecting apps.
        <StoreFirstRunWizard businessId={businessId} onDone={onDone} />
      ) : (
        <IntelligenceFirstRunWizard
          businessId={businessId}
          initialStep={resumeStep}
          onDone={onDone}
        />
      )}
    </Suspense>
  );
}
